import React, { useState } from "react";
import {
  Phone,
  Lock,
  Eye,
  EyeOff,
  ArrowLeft,
  AlertCircle,
  ClipboardCheck,
  Users,
  ShieldCheck,
  CheckCircle,
  MessageSquare,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import toast from "react-hot-toast";
import {
  loginUserPassword,
  usersendOtp,
  userverifyOtp,
  loginPurohitPassword,
  purohitsendOtp,
  purohitverifyOtp,
  loginTemplepassword,
  templesendOtp,
  templeverifyOtp,
} from "../../services/api";

const Login = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const registrationMessage = location.state?.message;
  const prefilledPhone = location.state?.phone || "";

  const [accountType, setAccountType] = useState("user");
  const [loginMethod, setLoginMethod] = useState("password");
  const [formData, setFormData] = useState({
    phone: prefilledPhone,
    password: "",
    otp: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [successMessage, setSuccessMessage] = useState(
    registrationMessage || ""
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    let newValue = value;

    if (name === "password") {
      newValue = value.replace(/\s/g, "");
    }

    if (name === "phone" || name === "otp") {
      newValue = value.replace(/\D/g, "");
    }

    setFormData((p) => ({ ...p, [name]: newValue }));

    if (name === "phone") {
      if (!newValue) {
        setErrors((p) => ({ ...p, phone: "Phone number is required" }));
      } else if (newValue.length !== 10) {
        setErrors((p) => ({
          ...p,
          phone: "Phone number must be 10 digits",
        }));
      } else {
        setErrors((p) => ({ ...p, phone: "" }));
      }
    }

    if (name === "password") {
      setErrors((p) => ({
        ...p,
        password: /\s/.test(value) ? "Password must not contain spaces" : "",
      }));
    }

    if (name === "otp") {
      if (!newValue) {
        setErrors((p) => ({ ...p, otp: "OTP is required" }));
      } else if (newValue.length !== 6) {
        setErrors((p) => ({ ...p, otp: "OTP must be 6 digits" }));
      } else {
        setErrors((p) => ({ ...p, otp: "" }));
      }
    }

    setTouched((p) => ({ ...p, [name]: true }));
  };

  const handleBlur = (field) => {
    setTouched((p) => ({ ...p, [field]: true }));
  };

  const validateAll = () => {
    const newErrors = {};

    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else if (formData.phone.length !== 10) {
      newErrors.phone = "Phone number must be 10 digits";
    }

    if (loginMethod === "password") {
      if (!formData.password) {
        newErrors.password = "Password is required";
      } else if (/\s/.test(formData.password)) {
        newErrors.password = "Password must not contain spaces";
      }
    } else {
      if (!formData.otp) {
        newErrors.otp = "OTP is required";
      } else if (formData.otp.length !== 6) {
        newErrors.otp = "OTP must be 6 digits";
      }
    }

    setErrors(newErrors);
    setTouched({
      phone: true,
      password: loginMethod === "password",
      otp: loginMethod === "otp",
    });

    return Object.keys(newErrors).length === 0;
  };

  const { login } = useAuth();

  const handleSendOTP = async () => {
    if (!formData.phone) {
      setErrors({ phone: "Phone number is required" });
      setTouched({ phone: true });
      return;
    }

    if (formData.phone.length !== 10) {
      setErrors({ phone: "Phone number must be 10 digits" });
      setTouched({ phone: true });
      return;
    }

    setLoading(true);
    try {
      let response;

      if (accountType === "user") {
        response = await usersendOtp({ phone: formData.phone });
      } else if (accountType === "purohit") {
        response = await purohitsendOtp({ phone: formData.phone });
      } else if (accountType === "temple") {
        response = await templesendOtp({ phone: formData.phone });
      }

      // Check if OTP was sent successfully
      // Backend returns: { message: "OTP generated successfully" } or { success: true }
      if (
        response.data.success ||
        response.data.message?.toLowerCase().includes("sent") ||
        response.data.message?.toLowerCase().includes("generated") ||
        response.data.message?.toLowerCase().includes("otp")
      ) {
        setOtpSent(true);
        toast.success("OTP sent successfully to your phone");

        // Auto-focus first OTP input
        setTimeout(() => {
          const firstInput = document.getElementById("otp-input-0");
          if (firstInput) firstInput.focus();
        }, 100);
      } else {
        toast.error(response.data.message || "Failed to send OTP");
      }
    } catch (err) {
      console.error("Send OTP error:", err);
      toast.error(
        err.response?.data?.message || "Failed to send OTP. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateAll()) return;

    setLoading(true);
    try {
      let response;

      if (loginMethod === "password") {
        const loginData = {
          phone: formData.phone,
          password: formData.password,
        };

        if (accountType === "user") {
          response = await loginUserPassword(loginData);
        } else if (accountType === "purohit") {
          response = await loginPurohitPassword(loginData);
        } else if (accountType === "temple") {
          response = await loginTemplepassword(loginData);
        }
      } else {
        const otpData = {
          phone: formData.phone,
          otp: formData.otp,
        };

        if (accountType === "user") {
          response = await userverifyOtp(otpData);
        } else if (accountType === "purohit") {
          response = await purohitverifyOtp(otpData);
        } else if (accountType === "temple") {
          response = await templeverifyOtp(otpData);
        }
      }

      const responseData = response.data;

      if (responseData.success || responseData.token) {
        const token = responseData.token;

        let userData = null;
        if (accountType === "user") {
          userData =
            responseData.user || responseData.data?.user || responseData.data;
        } else if (accountType === "purohit") {
          userData =
            responseData.purohit ||
            responseData.data?.purohit ||
            responseData.data;
        } else if (accountType === "temple") {
          userData =
            responseData.temple ||
            responseData.data?.temple ||
            responseData.data;
        }

        if (!token) {
          throw new Error("Authentication token missing in response");
        }

        if (!userData || !userData.id) {
          throw new Error("User data missing in response");
        }

        const authData = {
          isAuthenticated: true,
          user: userData,
          role: accountType,
          token: token,
        };

        localStorage.setItem("auth", JSON.stringify(authData));
        localStorage.setItem("currentUserId", userData.id);

        await login(authData);

        toast.success("Login successful!");

        setTimeout(() => {
          if (accountType === "purohit") {
            navigate("/purohit-dashboard");
          } else if (accountType === "temple") {
            navigate("/TempleSevasPage");
          } else {
            navigate("/");
          }
        }, 500);
      } else {
        toast.error(responseData.message || "Login failed");
      }
    } catch (err) {
      console.error("Login error:", err);

      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        (loginMethod === "password"
          ? "Invalid phone number or password"
          : "Invalid OTP");

      toast.error(errorMessage);
      setErrors({ general: errorMessage });
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    const digit = value.replace(/\D/g, "").slice(-1);

    const newOtp = formData.otp.split("");

    while (newOtp.length < 6) {
      newOtp.push("");
    }

    newOtp[index] = digit;

    const otpString = newOtp.join("");
    setFormData((p) => ({ ...p, otp: otpString }));
    setTouched((p) => ({ ...p, otp: true }));

    if (digit && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === "Backspace") {
      const newOtp = formData.otp.split("");

      while (newOtp.length < 6) {
        newOtp.push("");
      }

      if (!newOtp[index] && index > 0) {
        const prevInput = document.getElementById(`otp-input-${index - 1}`);
        if (prevInput) prevInput.focus();
      } else {
        newOtp[index] = "";
        const otpString = newOtp.join("");
        setFormData((p) => ({ ...p, otp: otpString }));
      }
    } else if (e.key === "Enter" && formData.otp.length === 6) {
      handleSubmit();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-yellow-50 to-orange-50 flex items-center justify-center p-3 sm:p-4 md:p-5 lg:p-6 xl:p-8 my-11">
      <div className="w-full max-w-6xl mx-auto">
        <div className="bg-white rounded-xl lg:rounded-2xl shadow-2xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="hidden lg:flex bg-gradient-to-br from-red-500 to-orange-500 text-white p-6 sm:p-7 md:p-8 lg:p-8 xl:p-10 flex-col justify-center">
              <div className="flex flex-col items-center mb-6 lg:mb-8">
                <div className="w-24 h-24 rounded-full shadow-xl bg-gradient-to-br from-orange-500 to-red-600 flex items-center justify-center flex-shrink-0">
                  <img src="/logo.jpg" alt="logo"
                    />
                </div>
                <div className="text-center lg:text-left">
                  <h1 className="text-2xl sm:text-2xl md:text-3xl lg:text-2xl xl:text-3xl 2xl:text-4xl font-serif font-bold mb-1.5 sm:mb-2 leading-tight">
                    Welcome to{" "}
                    <span className="text-white drop-shadow-lg">
                      Purohit App
                    </span>
                  </h1>
                  <p className="text-xs sm:text-sm md:text-base lg:text-sm xl:text-base text-orange-100 font-medium">
                    Sacred Services, Delivered with Devotion
                  </p>
                </div>
              </div>
              <div className="space-y-2.5 sm:space-y-3 lg:space-y-3 w-full max-w-md mx-auto lg:mx-0">
                <StatItem
                  icon={
                    <ClipboardCheck className="w-4 h-4 lg:w-4 lg:h-4 xl:w-5 xl:h-5" />
                  }
                  text="2000+ Verified Purohits"
                />
                <StatItem
                  icon={
                    <Users className="w-4 h-4 lg:w-4 lg:h-4 xl:w-5 xl:h-5" />
                  }
                  text="50,000+ Families Served"
                />
                <StatItem
                  icon={
                    <ShieldCheck className="w-4 h-4 lg:w-4 lg:h-4 xl:w-5 xl:h-5" />
                  }
                  text="100% Secure & Trusted"
                />
              </div>
            </div>

            <div className="p-6 sm:p-7 md:p-8 lg:p-8 xl:p-10 flex flex-col justify-center">
              <div className="w-full max-w-md mx-auto">
                <div className="-mt-3"></div>
                <button
                  onClick={() => navigate("/")}
                  className="flex items-center gap-2 text-sm font-semibold text-neutral-700 bg-white px-3 py-2 rounded-lg shadow-md hover:shadow-lg hover:bg-neutral-50 transition-all duration-200 border border-neutral-200 mb-4 lg:mb-5"
                >
                  <ArrowLeft className="w-4 h-4 text-orange-600" /> Back to Home
                </button>

                {successMessage && (
                  <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-green-700 text-sm flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" /> {successMessage}
                    </p>
                  </div>
                )}

                {errors.general && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-red-600 text-sm flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" /> {errors.general}
                    </p>
                  </div>
                )}

                <div className="mb-5 lg:mb-6">
                  <h2 className="text-2xl sm:text-2xl md:text-3xl lg:text-2xl xl:text-3xl font-serif font-bold text-center mb-1.5">
                    Welcome Back
                  </h2>
                  <p className="text-neutral-600 text-center text-sm md:text-base lg:text-sm xl:text-base">
                    Sign in to continue your spiritual journey
                  </p>
                </div>

                {/* Login Method Toggle */}
                <div className="mb-5 flex gap-2 p-1 bg-neutral-100 rounded-lg">
                  <button
                    type="button"
                    onClick={() => {
                      setLoginMethod("password");
                      setOtpSent(false);
                      setFormData((p) => ({ ...p, otp: "" }));
                      setErrors({});
                    }}
                    className={`flex-1 py-2.5 rounded-md font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                      loginMethod === "password"
                        ? "bg-white text-orange-600 shadow-md"
                        : "text-neutral-600 hover:text-neutral-800"
                    }`}
                  >
                    <Lock className="w-4 h-4" />
                    Password
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setLoginMethod("otp");
                      setFormData((p) => ({ ...p, password: "" }));
                      setErrors({});
                    }}
                    className={`flex-1 py-2.5 rounded-md font-medium text-sm transition-all duration-200 flex items-center justify-center gap-2 ${
                      loginMethod === "otp"
                        ? "bg-white text-orange-600 shadow-md"
                        : "text-neutral-600 hover:text-neutral-800"
                    }`}
                  >
                    <MessageSquare className="w-4 h-4" />
                    OTP
                  </button>
                </div>

                {/* Account Type Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Account Type
                  </label>
                  <select
                    value={accountType}
                    onChange={(e) => {
                      setAccountType(e.target.value);
                      setFormData({ phone: "", password: "", otp: "" });
                      setOtpSent(false);
                      setErrors({});
                      setTouched({});
                    }}
                    className="w-full text-sm px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                  >
                    <option value="user">User</option>
                    <option value="purohit">Purohit</option>
                    <option value="temple">Temple Seva</option>
                  </select>
                </div>

                {/* Phone Number Field */}
                <div className="mb-4 relative">
                  <label className="text-sm font-medium mb-1.5 block text-neutral-700">
                    Phone Number *
                  </label>

                  <div className="relative overflow-visible">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 lg:w-5 lg:h-5 text-neutral-400 z-10" />

                    <input
                      type="tel"
                      name="phone"
                      autoComplete="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      onBlur={() => handleBlur("phone")}
                      placeholder="Enter 10-digit mobile number"
                      maxLength="10"
                      className={`w-full pl-10 pr-3 py-2.5 lg:py-2.5 xl:py-3 text-sm border rounded-lg 
        focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent 
        transition-all ${
          touched.phone && errors.phone
            ? "border-red-400"
            : "border-neutral-300"
        }`}
                    />
                  </div>

                  <div className="min-h-[20px] mt-1.5">
                    {touched.phone && errors.phone && (
                      <p className="text-xs text-red-600 flex items-center gap-1.5">
                        <AlertCircle className="w-3.5 h-3.5" />
                        <span>{errors.phone}</span>
                      </p>
                    )}
                  </div>
                </div>

                {/* Password Field */}
                {loginMethod === "password" && (
                  <div className="mb-4 -mt-5">
                    <label className="text-sm font-medium mb-1.5 block text-neutral-700">
                      Password *
                    </label>

                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 lg:w-5 lg:h-5 text-neutral-400" />

                      <input
                        type={showPassword ? "text" : "password"}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        onBlur={() => handleBlur("password")}
                        placeholder="Enter your password"
                        onKeyPress={(e) => {
                          if (e.key === "Enter") handleSubmit();
                        }}
                        className={`w-full pl-10 pr-11 py-2.5 lg:py-2.5 xl:py-3 text-sm border rounded-lg 
        focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all ${
          touched.password && errors.password
            ? "border-red-400"
            : "border-neutral-300"
        }`}
                      />

                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-neutral-100 rounded transition-colors"
                      >
                        {showPassword ? (
                          <EyeOff className="w-4 h-4 lg:w-5 lg:h-5 text-neutral-500" />
                        ) : (
                          <Eye className="w-4 h-4 lg:w-5 lg:h-5 text-neutral-500" />
                        )}
                      </button>
                    </div>

                    <div className="min-h-[20px] mt-1.5">
                      {touched.password && errors.password && (
                        <p className="text-xs text-red-600 flex items-center gap-1.5">
                          <AlertCircle className="w-3.5 h-3.5" />
                          <span>{errors.password}</span>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {/* OTP Field */}
                {loginMethod === "otp" && (
                  <div className="-mt-4 mb-2">
                    {/* OTP Label */}
                    <label className="text-sm font-medium mb-2 block text-neutral-700">
                      OTP *
                    </label>

                    {/* OTP Inputs + Send OTP Button (Same Line) */}
                    <div className="flex items-center gap-3 mb-4">
                      {/* OTP Boxes */}
                      <div className="flex gap-2">
                        {[0, 1, 2, 3, 4, 5].map((index) => (
                          <input
                            key={index}
                            id={`otp-input-${index}`}
                            type="text"
                            inputMode="numeric"
                            maxLength="1"
                            value={formData.otp[index] || ""}
                            disabled={!otpSent}
                            onChange={(e) =>
                              handleOtpChange(index, e.target.value)
                            }
                            onKeyDown={(e) => handleOtpKeyDown(index, e)}
                            onPaste={(e) => {
                              e.preventDefault();
                              const pastedData = e.clipboardData
                                .getData("text")
                                .replace(/\D/g, "")
                                .slice(0, 6);

                              if (pastedData) {
                                setFormData((p) => ({ ...p, otp: pastedData }));
                                setTouched((p) => ({ ...p, otp: true }));

                                document
                                  .getElementById(
                                    `otp-input-${pastedData.length - 1}`
                                  )
                                  ?.focus();
                              }
                            }}
                            className={`w-10 h-10 text-center text-base font-semibold border-2 rounded-lg
              focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent
              ${
                touched.otp && errors.otp
                  ? "border-red-400"
                  : "border-neutral-300"
              }
              ${
                !otpSent
                  ? "bg-neutral-50 cursor-not-allowed"
                  : "bg-white cursor-text"
              }`}
                          />
                        ))}
                      </div>

                      {/* Send / Resend OTP Button */}
                      <button
                        type="button"
                        onClick={handleSendOTP}
                        disabled={
                          loading ||
                          !formData.phone ||
                          formData.phone.length !== 10
                        }
                        className="px-4 h-10 ml-12 bg-orange-500 hover:bg-orange-600 text-white 
          rounded-lg font-medium text-sm whitespace-nowrap 
          transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading
                          ? "Sending..."
                          : otpSent
                          ? "Resend OTP"
                          : "Send OTP"}
                      </button>
                    </div>

                    {/* Error */}
                    <div className="min-h-[20px] mb-2">
                      {touched.otp && errors.otp && (
                        <p className="text-xs text-red-600 flex items-center gap-1.5">
                          <AlertCircle className="w-3.5 h-3.5" />
                          <span>{errors.otp}</span>
                        </p>
                      )}
                    </div>
                  </div>
                )}

                {loginMethod === "password" && (
                  <div className="text-right mb-5 -mt-8">
                    <button
                      onClick={() => navigate("/forgot-password")}
                      className="text-sm text-orange-600 hover:text-orange-700 font-medium hover:underline transition-colors"
                    >
                      Forgot Password?
                    </button>
                  </div>
                )}

                <div className="-mt-2"></div>
                <button
                  onClick={handleSubmit}
                  disabled={loading || (loginMethod === "otp" && !otpSent)}
                  className="w-full min-h-[52px] py-3 lg:py-3 xl:py-3.5 
    bg-gradient-to-r from-orange-500 to-red-500 
    hover:from-orange-600 hover:to-red-600 
    text-white rounded-lg font-semibold text-sm md:text-base 
    shadow-lg hover:shadow-xl transition-all duration-300 
    disabled:opacity-70 disabled:cursor-not-allowed 
    transform hover:scale-[1.02] active:scale-[0.98]"
                >
                  {loading
                    ? "Logging in..."
                    : loginMethod === "otp" && !otpSent
                    ? "Send OTP First"
                    : "Sign in to Your Account"}
                </button>

                <div className="-mt-2"></div>
                <p className="text-sm text-center mt-3 lg:mt-4 text-neutral-600">
                  New here?{" "}
                  <button
                    onClick={() => navigate("/register")}
                    className="text-orange-600 hover:text-orange-700 font-bold hover:underline"
                  >
                    Create an account
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const StatItem = ({ icon, text }) => (
  <div className="flex items-center gap-2.5 sm:gap-3 text-white bg-white/20 backdrop-blur-sm p-2.5 sm:p-3 lg:p-3 xl:p-3.5 rounded-lg shadow-sm hover:bg-white/25 transition-all duration-300">
    <span className="flex-shrink-0">{icon}</span>
    <span className="font-semibold text-sm md:text-base lg:text-sm xl:text-base">
      {text}
    </span>
  </div>
);

export default Login;
